/**
 * FormBuilder — admin-facing drag-drop form designer.
 *
 * Three-pane layout:
 *   ┌──────────┬───────────────────┬──────────────┐
 *   │ Palette  │   Canvas          │  Field props │
 *   │ (types)  │   (sections+fields) │  (selected) │
 *   └──────────┴───────────────────┴──────────────┘
 *
 * Click a field-type chip in the palette → it appends to the active section.
 * Click a field on the canvas → its props open in the right panel.
 * Reorder fields with up/down arrows OR HTML5 native drag.
 *
 * No new npm deps — uses HTML5 draggable + arrow keys only.
 *
 * Props:
 *   value      — FormSchema object (controlled). Required.
 *   onChange   — (newSchema) => void.
 *   onPreview  — () => void. Optional. Shown as a "Preview" toggle button
 *                if provided; otherwise consumer can render their own preview
 *                separately by passing the schema to <FormRenderer />.
 *   readOnly   — boolean. Disables every edit control (used for "view this
 *                form's schema" admin views).
 */
import React, { useState, useMemo } from 'react';

const COLORS = {
  brand: '#3F66A0', brandTint: '#eef3fb',
  border: '#e2e8f0', borderHover: '#cbd5e1', borderActive: '#3F66A0',
  ink: '#0f172a', inkSoft: '#475569', inkMuted: '#94a3b8',
  red: '#dc2626', redTint: '#fef2f2',
  bg: '#fff', subtle: '#fafbfc',
};

// ─── Field-type catalog ─────────────────────────────────────────────
// Each entry: how it appears in the palette + the default field record
// dropped onto the canvas when clicked.
const FIELD_TYPES = [
  { type: 'text',        icon: 'Aa', label: 'Short text', sample: { type: 'text' } },
  { type: 'textarea',    icon: '¶',  label: 'Paragraph', sample: { type: 'textarea' } },
  { type: 'number',      icon: '#',  label: 'Number', sample: { type: 'number' } },
  { type: 'currency',    icon: '$',  label: 'Currency', sample: { type: 'currency' } },
  { type: 'date',        icon: '📅', label: 'Date', sample: { type: 'date' } },
  { type: 'datetime',    icon: '🕒', label: 'Date + time', sample: { type: 'datetime' } },
  { type: 'select',      icon: '▾',  label: 'Dropdown', sample: { type: 'select', options: [{ value: 'opt1', label: 'Option 1' }] } },
  { type: 'multiselect', icon: '☰',  label: 'Multi-select', sample: { type: 'multiselect', options: [{ value: 'opt1', label: 'Option 1' }] } },
  { type: 'checkbox',    icon: '☑',  label: 'Yes / No', sample: { type: 'checkbox' } },
  { type: 'file',        icon: '📎', label: 'File upload', sample: { type: 'file' } },
  { type: 'picker:user',     icon: '👤', label: 'User picker', sample: { type: 'picker', picker: 'user' } },
  { type: 'picker:property', icon: '🏢', label: 'Property picker', sample: { type: 'picker', picker: 'property' } },
  { type: 'picker:account',  icon: '💼', label: 'Account picker', sample: { type: 'picker', picker: 'account' } },
  { type: 'picker:truck',    icon: '🚚', label: 'Truck picker', sample: { type: 'picker', picker: 'truck' } },
];

// Generate a unique snake_case key from a label, ensuring no collision
function generateKey(label, existing) {
  const base = (label || 'field')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9]/, 'f$&');
  let key = base || 'field';
  let i = 1;
  while (existing.has(key)) {
    i += 1;
    key = `${base}_${i}`;
  }
  return key;
}

const cardStyle = {
  background: COLORS.bg,
  borderRadius: 12,
  padding: 16,
  border: `1px solid ${COLORS.border}`,
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 700,
  color: COLORS.inkSoft, marginBottom: 6, textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle = {
  width: '100%', boxSizing: 'border-box', fontSize: 14,
  padding: '10px 12px', border: `1.5px solid ${COLORS.border}`,
  borderRadius: 8, outline: 'none',
  background: '#fff', color: COLORS.ink, fontFamily: 'inherit',
};

// ─── Palette (left) ─────────────────────────────────────────────────
function Palette({ onAdd, disabled }) {
  return (
    <div style={cardStyle}>
      <h3 style={{ margin: 0, marginBottom: 12, fontSize: 13, fontWeight: 700,
        color: COLORS.ink, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Add Field
      </h3>
      <div style={{ display: 'grid', gap: 6 }}>
        {FIELD_TYPES.map((ft) => (
          <button
            key={ft.type}
            type="button"
            disabled={disabled}
            onClick={() => onAdd(ft)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', borderRadius: 8,
              border: `1px solid ${COLORS.border}`, background: '#fff',
              color: COLORS.ink, fontSize: 13, fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              textAlign: 'left', transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={(e) => !disabled && (e.currentTarget.style.borderColor = COLORS.borderActive,
                                              e.currentTarget.style.background = COLORS.brandTint)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = COLORS.border,
                                  e.currentTarget.style.background = '#fff')}
          >
            <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{ft.icon}</span>
            <span>{ft.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Canvas field row ───────────────────────────────────────────────
function FieldRow({ field, isActive, onSelect, onMoveUp, onMoveDown, onDelete, isFirst, isLast, onDragStart, onDragOver, onDrop }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => { e.preventDefault(); onDragOver(e); }}
      onDrop={onDrop}
      onClick={onSelect}
      style={{
        padding: '12px 14px', borderRadius: 10, marginBottom: 8,
        border: `1.5px solid ${isActive ? COLORS.brand : COLORS.border}`,
        background: isActive ? COLORS.brandTint : '#fff',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <span style={{ fontSize: 16, color: COLORS.inkMuted, cursor: 'grab' }}
        title="Drag to reorder">⋮⋮</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.ink, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {field.label || <em style={{ color: COLORS.inkMuted }}>(unlabeled)</em>}
          {field.required && <span style={{ color: COLORS.red, marginLeft: 4 }}>*</span>}
        </div>
        <div style={{ fontSize: 11, color: COLORS.inkMuted, marginTop: 2, display: 'flex', gap: 8, fontFamily: 'monospace' }}>
          <span>{field.type}{field.picker ? ':' + field.picker : ''}</span>
          <span>·</span>
          <span>{field.key}</span>
          {field.show_if && <><span>·</span><span>show_if {field.show_if.field}={String(field.show_if.equals)}</span></>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
          disabled={isFirst}
          style={{ padding: '4px 8px', border: `1px solid ${COLORS.border}`, background: '#fff',
            borderRadius: 6, fontSize: 11, color: isFirst ? COLORS.inkMuted : COLORS.inkSoft,
            cursor: isFirst ? 'not-allowed' : 'pointer' }}
          title="Move up">↑</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
          disabled={isLast}
          style={{ padding: '4px 8px', border: `1px solid ${COLORS.border}`, background: '#fff',
            borderRadius: 6, fontSize: 11, color: isLast ? COLORS.inkMuted : COLORS.inkSoft,
            cursor: isLast ? 'not-allowed' : 'pointer' }}
          title="Move down">↓</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{ padding: '4px 8px', border: `1px solid #fecaca`, background: '#fff',
            borderRadius: 6, fontSize: 11, color: '#b91c1c', cursor: 'pointer' }}
          title="Delete">×</button>
      </div>
    </div>
  );
}

// ─── Canvas (middle) ────────────────────────────────────────────────
function Canvas({ schema, activeFieldKey, onSelectField, onSchemaChange, dragRef }) {
  const updateSchema = (mut) => {
    const next = JSON.parse(JSON.stringify(schema));
    mut(next);
    onSchemaChange(next);
  };

  const moveField = (sectionIdx, fieldIdx, delta) => {
    updateSchema((next) => {
      const fields = next.sections[sectionIdx].fields;
      const target = fieldIdx + delta;
      if (target < 0 || target >= fields.length) return;
      const [moved] = fields.splice(fieldIdx, 1);
      fields.splice(target, 0, moved);
    });
  };

  const deleteField = (sectionIdx, fieldIdx) => {
    updateSchema((next) => {
      const fields = next.sections[sectionIdx].fields;
      const [removed] = fields.splice(fieldIdx, 1);
      // Also strip any show_if rules that referenced this field's key
      for (const sec of next.sections) {
        for (const f of sec.fields) {
          if (f.show_if && f.show_if.field === removed.key) delete f.show_if;
        }
      }
    });
  };

  const addSection = () => {
    updateSchema((next) => {
      next.sections.push({ title: `Section ${next.sections.length + 1}`, fields: [] });
    });
  };

  const updateSection = (sectionIdx, patch) => {
    updateSchema((next) => {
      Object.assign(next.sections[sectionIdx], patch);
    });
  };

  const deleteSection = (sectionIdx) => {
    if (schema.sections.length <= 1) return;  // always keep at least one section
    if (!window.confirm('Delete this section and every field in it?')) return;
    updateSchema((next) => {
      next.sections.splice(sectionIdx, 1);
    });
  };

  // Drag handlers — index encoded as "sectionIdx:fieldIdx" for cross-section drops
  const handleDragStart = (e, sectionIdx, fieldIdx) => {
    dragRef.current = { sectionIdx, fieldIdx };
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDrop = (e, sectionIdx, fieldIdx) => {
    const src = dragRef.current;
    if (!src) return;
    if (src.sectionIdx === sectionIdx && src.fieldIdx === fieldIdx) return;
    updateSchema((next) => {
      const [moved] = next.sections[src.sectionIdx].fields.splice(src.fieldIdx, 1);
      // adjust target index if the source removed a slot before it in the same section
      let dst = fieldIdx;
      if (src.sectionIdx === sectionIdx && src.fieldIdx < fieldIdx) dst -= 1;
      next.sections[sectionIdx].fields.splice(dst, 0, moved);
    });
    dragRef.current = null;
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Form-level title */}
      <div style={cardStyle}>
        <label style={labelStyle}>Form Title</label>
        <input
          type="text"
          value={schema.title || ''}
          placeholder="e.g. Property Site Inspection Request"
          onChange={(e) => onSchemaChange({ ...schema, title: e.target.value })}
          style={inputStyle}
        />
      </div>

      {schema.sections.map((section, sIdx) => (
        <div key={sIdx} style={{ ...cardStyle, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <input
              type="text"
              value={section.title || ''}
              placeholder={`Section ${sIdx + 1}`}
              onChange={(e) => updateSection(sIdx, { title: e.target.value })}
              style={{ ...inputStyle, fontWeight: 700, color: COLORS.ink }}
            />
            {schema.sections.length > 1 && (
              <button type="button" onClick={() => deleteSection(sIdx)}
                style={{ padding: '8px 12px', border: `1px solid #fecaca`, background: '#fff',
                  color: '#b91c1c', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap' }}>Delete section</button>
            )}
          </div>
          <input
            type="text"
            value={section.description || ''}
            placeholder="Optional helper text shown under the section title"
            onChange={(e) => updateSection(sIdx, { description: e.target.value })}
            style={{ ...inputStyle, marginBottom: 14, fontSize: 13, color: COLORS.inkSoft }}
          />

          {section.fields.length === 0 ? (
            <div style={{
              padding: '24px', borderRadius: 8, border: `2px dashed ${COLORS.border}`,
              textAlign: 'center', color: COLORS.inkMuted, fontSize: 13,
            }}>
              No fields yet. Click a field type on the left to add one.
            </div>
          ) : (
            section.fields.map((field, fIdx) => (
              <FieldRow
                key={field.key}
                field={field}
                isActive={activeFieldKey === field.key}
                isFirst={fIdx === 0}
                isLast={fIdx === section.fields.length - 1}
                onSelect={() => onSelectField(field.key)}
                onMoveUp={() => moveField(sIdx, fIdx, -1)}
                onMoveDown={() => moveField(sIdx, fIdx, 1)}
                onDelete={() => deleteField(sIdx, fIdx)}
                onDragStart={(e) => handleDragStart(e, sIdx, fIdx)}
                onDragOver={() => {}}
                onDrop={(e) => handleDrop(e, sIdx, fIdx)}
              />
            ))
          )}
        </div>
      ))}

      <button type="button" onClick={addSection}
        style={{
          padding: '12px', borderRadius: 10, border: `2px dashed ${COLORS.borderHover}`,
          background: COLORS.subtle, color: COLORS.inkSoft, fontSize: 13, fontWeight: 600,
          cursor: 'pointer',
        }}>+ Add Section</button>
    </div>
  );
}

// ─── Field properties panel (right) ─────────────────────────────────
function FieldProps({ schema, fieldKey, onSchemaChange }) {
  // ALL hooks must run on every render — call them before any early return.
  // Find the (sectionIdx, fieldIdx) for the active field.
  const location = useMemo(() => {
    if (!fieldKey) return null;
    for (let s = 0; s < schema.sections.length; s++) {
      const f = schema.sections[s].fields.findIndex(x => x.key === fieldKey);
      if (f !== -1) return { s, f };
    }
    return null;
  }, [schema, fieldKey]);

  // Available "show_if" sources = every field that currently sits BEFORE this one
  const showIfSources = useMemo(() => {
    const result = [];
    if (!location) return result;
    for (let s = 0; s < schema.sections.length; s++) {
      for (let f = 0; f < schema.sections[s].fields.length; f++) {
        if (s > location.s || (s === location.s && f >= location.f)) break;
        const cand = schema.sections[s].fields[f];
        if (cand.type === 'select' || cand.type === 'checkbox') {
          result.push(cand);
        }
      }
    }
    return result;
  }, [schema, location]);

  if (!location) {
    return (
      <div style={{ ...cardStyle, color: COLORS.inkMuted, fontSize: 13 }}>
        Click a field on the canvas to edit its properties.
      </div>
    );
  }

  const field = schema.sections[location.s].fields[location.f];

  const updateField = (patch) => {
    const next = JSON.parse(JSON.stringify(schema));
    Object.assign(next.sections[location.s].fields[location.f], patch);
    onSchemaChange(next);
  };

  const renameKey = (newKey) => {
    if (!/^[a-z][a-z0-9_]*$/.test(newKey)) return;
    const next = JSON.parse(JSON.stringify(schema));
    next.sections[location.s].fields[location.f].key = newKey;
    // Update any show_if references
    for (const sec of next.sections) {
      for (const f of sec.fields) {
        if (f.show_if && f.show_if.field === fieldKey) {
          f.show_if = { ...f.show_if, field: newKey };
        }
      }
    }
    onSchemaChange(next);
  };

  return (
    <div style={{ ...cardStyle, display: 'grid', gap: 14 }}>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: COLORS.ink,
        textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Field Properties
      </h3>

      <div>
        <label style={labelStyle}>Label</label>
        <input
          type="text"
          value={field.label || ''}
          onChange={(e) => updateField({ label: e.target.value })}
          style={inputStyle}
        />
      </div>

      <div>
        <label style={labelStyle}>Field Key</label>
        <input
          type="text"
          value={field.key}
          onChange={(e) => renameKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
          style={{ ...inputStyle, fontFamily: 'monospace' }}
        />
        <div style={{ fontSize: 11, color: COLORS.inkMuted, marginTop: 4 }}>
          snake_case, must be unique. Used as the JSON key for the value submitted.
        </div>
      </div>

      <div>
        <label style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={!!field.required}
            onChange={(e) => updateField({ required: e.target.checked })}
            style={{ accentColor: COLORS.brand }}
          />
          Required
        </label>
      </div>

      <div>
        <label style={labelStyle}>Helper Text</label>
        <input
          type="text"
          value={field.help || ''}
          placeholder="Shown beneath the field, gray, 12px"
          onChange={(e) => updateField({ help: e.target.value })}
          style={inputStyle}
        />
      </div>

      <div>
        <label style={labelStyle}>Placeholder</label>
        <input
          type="text"
          value={field.placeholder || ''}
          onChange={(e) => updateField({ placeholder: e.target.value })}
          style={inputStyle}
        />
      </div>

      {(field.type === 'select' || field.type === 'multiselect') && (
        <OptionsEditor field={field} updateField={updateField} />
      )}

      {field.type === 'file' && (
        <>
          <div>
            <label style={labelStyle}>Allowed File Types</label>
            <input
              type="text"
              value={field.accept || ''}
              placeholder=".pdf,.png,.jpg (comma-separated)"
              onChange={(e) => updateField({ accept: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Max File Size (MB)</label>
            <input
              type="number"
              value={field.max_file_mb ?? ''}
              min="1" max="25"
              onChange={(e) => updateField({ max_file_mb: e.target.value === '' ? undefined : Number(e.target.value) })}
              style={inputStyle}
            />
          </div>
        </>
      )}

      {(field.type === 'number' || field.type === 'currency') && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Min</label>
            <input
              type="number"
              value={field.min ?? ''}
              onChange={(e) => updateField({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Max</label>
            <input
              type="number"
              value={field.max ?? ''}
              onChange={(e) => updateField({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
              style={inputStyle}
            />
          </div>
        </div>
      )}

      <hr style={{ border: 'none', borderTop: `1px solid ${COLORS.border}`, margin: '4px 0' }} />

      <div>
        <label style={labelStyle}>Conditional Visibility</label>
        {showIfSources.length === 0 ? (
          <div style={{ fontSize: 12, color: COLORS.inkMuted }}>
            Add a select or checkbox field above this one to enable conditional visibility.
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <select
                value={field.show_if?.field || ''}
                onChange={(e) => updateField({
                  show_if: e.target.value ? { field: e.target.value, equals: '' } : undefined,
                })}
                style={inputStyle}
              >
                <option value="">— Always show —</option>
                {showIfSources.map(s => (
                  <option key={s.key} value={s.key}>{s.label} ({s.key})</option>
                ))}
              </select>
              {field.show_if && (() => {
                const src = showIfSources.find(s => s.key === field.show_if.field);
                if (!src) return null;
                if (src.type === 'checkbox') {
                  return (
                    <select
                      value={String(field.show_if.equals)}
                      onChange={(e) => updateField({ show_if: { ...field.show_if, equals: e.target.value === 'true' } })}
                      style={inputStyle}
                    >
                      <option value="true">is checked</option>
                      <option value="false">is unchecked</option>
                    </select>
                  );
                }
                return (
                  <select
                    value={field.show_if.equals || ''}
                    onChange={(e) => updateField({ show_if: { ...field.show_if, equals: e.target.value } })}
                    style={inputStyle}
                  >
                    <option value="">— pick a value —</option>
                    {(src.options || []).map(o => (
                      <option key={o.value} value={o.value}>= {o.label}</option>
                    ))}
                  </select>
                );
              })()}
            </div>
            <div style={{ fontSize: 11, color: COLORS.inkMuted, marginTop: 4 }}>
              Field will only appear (and be required) when the source matches.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function OptionsEditor({ field, updateField }) {
  const options = field.options || [];
  const setOption = (idx, patch) => {
    const next = options.slice();
    next[idx] = { ...next[idx], ...patch };
    updateField({ options: next });
  };
  const remove = (idx) => updateField({ options: options.filter((_, i) => i !== idx) });
  const add = () => updateField({ options: [...options, { value: `opt${options.length + 1}`, label: `Option ${options.length + 1}` }] });
  return (
    <div>
      <label style={labelStyle}>Options</label>
      <div style={{ display: 'grid', gap: 6 }}>
        {options.map((o, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6 }}>
            <input
              type="text"
              value={o.label}
              placeholder="Label shown to user"
              onChange={(e) => setOption(i, { label: e.target.value })}
              style={inputStyle}
            />
            <input
              type="text"
              value={o.value}
              placeholder="Stored value"
              onChange={(e) => setOption(i, { value: e.target.value })}
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={options.length <= 1}
              style={{
                padding: '6px 10px', border: `1px solid #fecaca`, background: '#fff',
                color: '#b91c1c', borderRadius: 6, fontSize: 12,
                cursor: options.length <= 1 ? 'not-allowed' : 'pointer',
              }}
              title="Remove option"
            >×</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        style={{ marginTop: 8, padding: '8px 12px', border: `1px solid ${COLORS.border}`,
          background: '#fff', color: COLORS.brand, borderRadius: 6, fontSize: 12, fontWeight: 600,
          cursor: 'pointer' }}>+ Add option</button>
    </div>
  );
}

// ─── The exported component ─────────────────────────────────────────
export default function FormBuilder({ value, onChange, readOnly = false }) {
  const dragRef = React.useRef(null);
  const [activeFieldKey, setActiveFieldKey] = useState(null);

  // Click-to-add: pop a default field for this type into the last section
  const addField = (ft) => {
    if (readOnly) return;
    const allKeys = new Set();
    for (const s of value.sections) for (const f of s.fields) allKeys.add(f.key);
    const key = generateKey(ft.label, allKeys);
    const newField = { key, label: ft.label, required: false, ...ft.sample };
    const next = JSON.parse(JSON.stringify(value));
    const targetSection = next.sections[next.sections.length - 1];
    targetSection.fields.push(newField);
    onChange(next);
    setActiveFieldKey(key);
  };

  return (
    <div className="form-builder-grid" style={{
      display: 'grid',
      gridTemplateColumns: '220px 1fr 320px',
      gap: 16,
      alignItems: 'flex-start',
    }}>
      <Palette onAdd={addField} disabled={readOnly} />
      <Canvas
        schema={value}
        activeFieldKey={activeFieldKey}
        onSelectField={(k) => setActiveFieldKey(k)}
        onSchemaChange={onChange}
        dragRef={dragRef}
      />
      <FieldProps
        schema={value}
        fieldKey={activeFieldKey}
        onSchemaChange={onChange}
      />
    </div>
  );
}
